import React, { useEffect, useState } from 'react';
import { FiPlus, FiMoreHorizontal } from 'react-icons/fi';
import dragAndDrop from '@/assets/images/icons/drag-and-drop.svg';
import { Dropdown } from '@/components/custom/dropdown/dropdown';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { EmptyState } from '@/components/custom/empty-state';
import { confirm } from '@/lib/confirm';
import MatterModal from '../CreateExam/StepName/MatterModal';
import { Loader } from '@/components/custom/loader';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/lib/i18n/i18n';
import { useSession } from '@/hooks/useSession';
import usePermission from '@/hooks/usePermission';
import { useLocation } from 'react-router-dom';
import { TaxonomiesIcon, TaxonomiesItem, TaxonomiesText } from '@/components/custom/taxonomies';

export default function MatterList(props) {
  const location = useLocation();
  const path = location.pathname;
  const matterCountType = path.includes('/apostilas') ? 'train' : path.includes('/simulados') ? 'open' : 'exam';
  const { selectedExam, setTaxonomyId, selectedTaxonomy, setSelectedExam, loading, setExams, exams, setCount, count, getTaxonomies, taxonomies } = props;
  const client = useClient();
  const [filteredMattersList, setFilteredMattersList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedMatters, setSelectedMatters] = useState([]);
  const [variable, setVariable] = useState();
  const currentUser = useSession().session.user;
  const { t } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(process.env.REACT_APP_LOCALE);
  }, []);

  useEffect(() => {
    if (path.includes('/apostilas')) {
      setVariable(t('sideBar.questionBooks'));
    } else if (path.includes('simulados')) {
      setVariable(t('textsCommon.simulations'));
    } else {
      setVariable(t('exams.toggleExams'));
    }
  }, [path]);

  useEffect(() => {
    if (showModal && taxonomies?.length === 0) {
      getTaxonomies();
    }
  }, [showModal]);

  const addMatter = async matter => {
    const { error, data } = await client.mutate(['question_books', selectedExam.id], { 'taxonomy-ids': [matter.id] });
    if (error) {
      toast.error(t('toast.errorAddSubject'));
    } else {
      const selectedMatter = data?.['question-book-taxonomies']?.find(item => item.taxonomy_id === matter.id);
      const newSelectedMatters = selectedMatters;
      newSelectedMatters.push({ ...selectedMatter, name: matter.name, taxonomy_id: matter.id });
      setSelectedMatters(newSelectedMatters);
      const newExams = exams.map(exam => {
        if (selectedExam.id == exam.id) {
          exam['question-book-taxonomies'] = newSelectedMatters;
          setSelectedExam(exam);
        }
        return exam;
      });
      setExams(newExams);
      toast.success(t('toast.successAddSubject'));
    }
  };

  const addedMatters = matter => {
    setSelectedMatters([...selectedMatters, matter]);
  };

  useEffect(() => {
    if (selectedExam) {
      setSelectedMatters([...selectedExam?.['question-book-taxonomies']]);
    }
  }, [exams, selectedExam]);

  const removeMatter = async argument => {
    try {
      const taxonomy = !showModal ? argument : selectedMatters.find(item => item.taxonomy_id === argument.id);

      await client.delete(['question_book_taxonomies', taxonomy.id]);

      const newExams = exams.map(exam => {
        if (selectedExam.id == exam.id) {
          exam['question-book-taxonomies'] = exam['question-book-taxonomies'].filter(item => parseInt(item.taxonomy_id) !== parseInt(argument.id));
          setSelectedExam(exam);
        }
        return exam;
      });
      setExams(newExams);
      // navigate(`/provas/gerenciar/${selectedExam.id}`);
      toast.success(t('toast.successSubjectRemoved'));
    } catch {
      toast.error(t('toast.errorSubjectRemoved'));
    }
  };

  useEffect(() => {
    setSearchTerm('');
  }, [selectedExam]);

  useEffect(() => {
    const filteredMattersList = selectedExam?.['question-book-taxonomies'];
    setFilteredMattersList(filteredMattersList);

    if (searchTerm !== '') {
      const searchMatter = filteredMattersList.filter(item => {
        if (searchTerm == '') {
          return item;
        }
        if (item.name != null && item.name.toLowerCase().includes(searchTerm.toLocaleLowerCase())) {
          return item;
        }
      });
      setFilteredMattersList(searchMatter);
    }
  }, [searchTerm, selectedExam, exams, filteredMattersList]);

  const handleDeleteMatter = async matter => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteSubject'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      removeMatter(matter);
    }
  };

  const hasManagePermission = usePermission('Gerenciar provas', 'Provas e simulados');
  const isCreatorOrAdmin = !selectedExam?.['authored-by-user'] || (selectedExam?.['authored-by-user'] && currentUser.id === selectedExam?.creator.id) || currentUser?.profileNames.includes('Administrador');

  return (
    <div className="new-questions__sidebar pt-14">
      <h3
        htmlFor="search"
        className="mb-11 w-full border-b border-neutral-lightest pb-4 text-base font-bold text-primary-dark"
      >
        {t('textsCommon.matter')}
      </h3>
      <input
        aria-label={t('textsCommon.research')}
        className="form__control form__control--search-with-icon"
        placeholder={t('textsCommon.research')}
        type="search"
        name="search"
        id="search"
        onChange={e => setSearchTerm(e.target.value)}
      />

      {hasManagePermission && isCreatorOrAdmin && (
        <fieldset className="form__row">
          <button
            type="button"
            className="btn btn--outline-dashed btn--small btn--full"
            onClick={() => {
              if (selectedExam?.published) {
                toast.error(t('toast.errorAddSubjectPublished'));
              } else {
                setShowModal(true);
              }
            }}
            disabled={!selectedExam}
          >
            <FiPlus className="btn__icon" /> {t('textsCommon.buttonAddMatters')}
          </button>

          <MatterModal
            addedMatters={addedMatters}
            addMatter={addMatter}
            removeMatter={removeMatter}
            selectedMatters={selectedMatters}
            matters={taxonomies}
            showModal={showModal}
            countType={matterCountType}
            onClose={() => {
              setShowModal(false);
              setCount(count + 1);
            }}
            loading={loading}
          />
        </fieldset>
      )}
      {loading && <Loader />}
      {!loading && filteredMattersList?.length === 0 ? (
        <EmptyState
          type="data"
          text={t('emptyState.noSubjectAdd')}
        />
      ) : (
        <Droppable droppableId="droppable">
          {provided => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {filteredMattersList?.map((item, i) => {
                return (
                  <Draggable
                    key={i}
                    draggableId={item?.id?.toString()}
                    index={i}
                  >
                    {provided => (
                      <div
                        key={i}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <TaxonomiesItem
                          active={selectedTaxonomy?.id === item?.id}
                          onClick={() => {
                            if (variable === 'Provas') {
                              window.history.pushState({ id: selectedExam.id, taxonomyId: item?.id }, '', `/provas/gerenciar/${selectedExam?.id}/${item?.id}`);
                            } else if (variable === 'Exercícios') {
                              window.history.pushState({ id: selectedExam.id, taxonomyId: item?.id }, '', `/apostilas/gerenciar/${selectedExam?.id}/${item?.id}`);
                            } else {
                              window.history.pushState({ id: selectedExam.id, taxonomyId: item?.id }, '', `/simulados/gerenciar/${selectedExam?.id}/${item?.id}`);
                            }
                            setTaxonomyId(item?.id);
                          }}
                        >
                          <TaxonomiesIcon
                            src={dragAndDrop}
                            alt=""
                          />
                          <TaxonomiesText title={item.name}>{item.name}</TaxonomiesText>
                          {hasManagePermission && isCreatorOrAdmin && (
                            <Dropdown>
                              <Dropdown.Toggle>
                                <FiMoreHorizontal />
                              </Dropdown.Toggle>

                              <Dropdown.Content>
                                <Dropdown.Item
                                  onClick={() => {
                                    handleDeleteMatter(item);
                                  }}
                                >
                                  {t('button.delete')}
                                </Dropdown.Item>
                              </Dropdown.Content>
                            </Dropdown>
                          )}
                        </TaxonomiesItem>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
}
